<template>
  <div
    :class="{
      'minimal-media-root': isMinimalView,
      'branded-media-root': isBrandedView,
    }"
  >
    <v-row
      justify="center"
      :class="{ 'ma-0': isMinimalView || isBrandedView, 'branded-media-row': isBrandedView }"
    >
      <v-col
        v-if="!loading"
        :cols="12"
        :md="isMinimalView || isBrandedView ? 12 : 11"
        :class="{ 'pa-0': isMinimalView || isBrandedView, 'branded-media-col': isBrandedView }"
      >
        <fullscreen
          ref="fullscreen"
          @change="onFullscreenChange"
          :style="fullscreenWrapperStyle"
        >
          <v-btn
            v-if="$fullscreen.support && !isMinimalView && !isBrandedView && data.file_type === 'genially'"
            type="button"
            class="btn btn-default btn-map-fullscreen fullscreen-align-right"
            @click="toggleFullscreen"
          >
            <v-icon>{{ fullscreen ? 'mdi-fullscreen-exit' : 'mdi-arrow-expand' }}</v-icon>
          </v-btn>

          <div v-if="data.file_type == 'url'" :class="{ 'branded-embed-fill': isBrandedView }">
            <v-img
              v-if="fileType == 'png' || fileType == 'jpg' || fileType == 'jpeg'"
              contain
              :height="isMinimalView || isBrandedView ? '100%' : undefined"
              :src="externalLinkSrc"
            />
            <iframe v-else :src="externalLinkSrc" :style="iframeStyle" />
          </div>

          <div
            v-else-if="data.file_type == 'canva'"
            :class="embedFillClass('canva-embed')"
          >
            <iframe
              :class="isMinimalView || isBrandedView ? 'minimal-iframe-fill' : 'canva-iframe'"
              :src="`${data.file_upload}?embed`"
            />
          </div>

          <div v-else-if="data.file_type == 'genially'" class="position-relative" :class="{ 'branded-embed-fill': isBrandedView }">
            <div
              v-if="showRotationCt && data.rotation_horizontal"
              class="rotation-ct showRotationCt"
            >
              <div class="rotation-item">
                <div class="phone" />
                <div class="message">Por favor, gire su dispositivo.</div>
              </div>
            </div>
            <div :class="isMinimalView || isBrandedView ? 'minimal-embed-fill branded-embed-fill' : ''">
              <div :class="isMinimalView || isBrandedView ? 'minimal-embed-fill branded-embed-fill' : 'genially-ratio'">
                <iframe
                  frameborder="0"
                  class="minimal-iframe-fill"
                  :src="data.file_upload"
                  allowfullscreen
                />
              </div>
            </div>
          </div>

          <v-img
            v-else-if="data.file_type == 'imagen'"
            contain
            :height="isMinimalView || isBrandedView ? '100%' : undefined"
            :src="data.file_upload"
          />

          <div
            v-else-if="data.file_type == 'video'"
            :class="[
              'video-player-container',
              {
                'minimal-video': isMinimalView,
                'branded-video': isBrandedView,
              },
            ]"
          >
            <vue-plyr
              ref="videoPlyr"
              :options="videoPlyrOptions"
            >
              <video
                controls
                playsinline
                preload="metadata"
                :poster="videoPoster"
                @timeupdate="emitVideoTimeUpdate"
                @ended="emitVideoEnded"
              >
                <template
                  v-if="data.video && data.video.resolutions && data.video.resolutions.length > 0"
                >
                  <source
                    v-for="(resolution, index) in data.video.resolutions"
                    :key="`source-${index}-${resolution.height}`"
                    :src="resolution.url"
                    type="video/mp4"
                    :size="resolution.height"
                  />
                </template>
                <template v-else>
                  <source :src="data.file_upload" type="video/mp4" />
                </template>
              </video>
            </vue-plyr>

            <TimelineMarkers
              v-if="videoDuration > 0 && unifiedMarkers.length > 0"
              :markers="unifiedMarkers"
              :current-time="currentVideoTime"
              :duration="videoDuration"
              :target-container="targetProgressBar"
              @marker-click="handleMarkerClick"
              @marker-hover="handleMarkerHover"
              @marker-leave="handleMarkerLeave"
            />

            <MarkerTooltip
              v-if="tooltip.visible && tooltip.marker"
              :marker="tooltip.marker"
              :position="tooltip.position"
            />

            <div
              v-if="$scopedSlots['video-actions']"
              class="video-player-actions"
            >
              <slot name="video-actions" />
            </div>
          </div>

          <div
            v-else-if="data.file_type == 'audio'"
            :class="isMinimalView || isBrandedView ? 'pa-4 minimal-audio' : 'mt-15 pt-15'"
          >
            <vue-plyr>
              <audio style="width: 100%" controls>
                <source :src="data.file_upload" />
              </audio>
            </vue-plyr>
          </div>

          <div
            v-else-if="data.file_type == 'documento' && isPdfDocument"
            :class="['document-pdf-viewer', { 'branded-embed-fill': isBrandedView }]"
          >
            <iframe
              :src="googleGviewUrl"
              :style="iframeStyle"
            />
          </div>

          <div
            v-else-if="data.file_type == 'documento' && !isPdfDocument"
            :class="['document-download-panel', { 'document-download-panel--minimal': isMinimalView || isBrandedView }]"
          >
            <v-card class="document-download-card" elevation="2">
              <v-card-text class="text-center pa-6">
                <v-avatar :color="documentTypeInfo.color" size="72" class="mb-4">
                  <v-icon color="white" size="36">{{ documentTypeInfo.icon }}</v-icon>
                </v-avatar>

                <h3 class="document-download-title mb-2">
                  {{ documentFileName }}
                </h3>

                <div class="document-download-meta mb-4">
                  <v-chip small class="mr-2" color="#EEEEEE">
                    {{ documentTypeInfo.label }}
                  </v-chip>
                  <v-chip v-if="documentExtension" small class="mr-2" color="#EEEEEE">
                    .{{ documentExtension }}
                  </v-chip>
                  <v-chip small color="#EEEEEE">
                    <v-progress-circular
                      v-if="fetchingFileSize"
                      indeterminate
                      size="14"
                      width="2"
                      class="mr-1"
                    />
                    {{ displayFileSize || 'Tamaño no disponible' }}
                  </v-chip>
                </div>

                <p class="document-download-hint mb-5">
                  Este tipo de archivo no se puede previsualizar en el navegador.
                  Descárgalo para abrirlo con la aplicación correspondiente.
                </p>

                <v-btn
                  color="#0DB2D2"
                  dark
                  large
                  rounded
                  class="px-8"
                  @click="downloadDocument"
                >
                  <v-icon left>mdi-download</v-icon>
                  Descargar {{ documentTypeInfo.label }}
                </v-btn>
              </v-card-text>
            </v-card>
          </div>

          <iframe
            v-else-if="data.file_upload"
            :src="googleGviewUrl"
            :style="iframeStyle"
          />
        </fullscreen>
      </v-col>
      <v-col
        v-else
        cols="12"
        :md="isMinimalView || isBrandedView ? 12 : 11"
        class="d-flex justify-center align-center"
      >
        <v-progress-circular size="120" indeterminate color="primary" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import fullscreen from 'vue-fullscreen';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { resolveExternalLinkUrl } from '../../utils/externalLinkUrl';
import {
  fetchRemoteFileSize,
  formatFileSize,
  getDocumentTypeInfo,
  getFileExtension,
  getGoogleGviewUrl,
  isPdfDocument as checkIsPdfDocument,
  resolveDocumentFilename,
} from '../../utils/documentFile';
import TimelineMarkers from '../VideoPlayer/timeline/TimelineMarkers.vue';
import MarkerTooltip from '../VideoPlayer/timeline/MarkerTooltip.vue';

Vue.use(fullscreen);

export default {
  name: 'ShowContentMedia',
  components: {
    TimelineMarkers,
    MarkerTooltip,
  },
  props: {
    data: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    fileType: { type: String, default: '' },
    showRotationCt: { type: Boolean, default: false },
    isMinimalView: { type: Boolean, default: false },
    isBrandedView: { type: Boolean, default: false },
    fullscreen: { type: Boolean, default: false },
    scheduleId: { type: [String, Number], default: null },
    notes: { type: Array, default: () => [] },
    polls: { type: Array, default: () => [] },
    answeredPollIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      resolvedFileSize: null,
      fetchingFileSize: false,
      lastEmittedVideoSecond: null,
      videoDuration: 0,
      targetProgressBar: null,
      currentVideoTime: 0,
      tooltip: {
        visible: false,
        marker: null,
        position: { x: 0, y: 0 },
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['AUTH_USER']),

    unifiedMarkers() {
      const parseTime = (obj) => {
        if (!obj) return 0;
        const val =
          obj.time_seconds ??
          obj.second ??
          obj.seconds ??
          obj.at_second ??
          obj.trigger_time ??
          obj.time_sec ??
          obj.time;
        const num = Number(val);
        return Number.isFinite(num) && num >= 0 ? num : 0;
      };

      const noteMarkers = (this.notes || []).map((n, i) => ({
        id: n.id || `note-${i}-${parseTime(n)}`,
        type: 'note',
        time: parseTime(n),
        data: {
          title: 'Nota',
          content: n.note || n.content || n.body || '',
        },
      }));

      const pollMarkers = (this.polls || []).map((p, i) => ({
        id: p.id || `poll-${i}-${parseTime(p)}`,
        type: 'survey',
        time: parseTime(p),
        data: {
          question: p.question || p.title || 'Encuesta',
          options: p.options || [],
          answered: Array.isArray(this.answeredPollIds)
            ? this.answeredPollIds.includes(p.id)
            : Boolean(p.answered || p.user_answer),
        },
      }));

      return [...noteMarkers, ...pollMarkers];
    },
    documentFileName() {
      return resolveDocumentFilename(this.data);
    },
    documentExtension() {
      return getFileExtension(this.data.file_upload, this.documentFileName);
    },
    isPdfDocument() {
      return checkIsPdfDocument(this.data.file_upload, this.documentFileName);
    },
    documentTypeInfo() {
      return getDocumentTypeInfo(this.documentExtension);
    },
    displayFileSize() {
      const size = this.resolvedFileSize ?? this.data.file_size;
      return formatFileSize(size);
    },
    googleGviewUrl() {
      if (!this.data.file_upload) {
        return '';
      }
      return getGoogleGviewUrl(this.data.file_upload);
    },
    externalLinkSrc() {
      if (this.data.file_type !== 'url' || !this.data.file_upload) {
        return this.data.file_upload;
      }
      return resolveExternalLinkUrl(
        this.data.file_upload,
        this.AUTH_USER,
        this.scheduleId
      );
    },
    fullscreenWrapperStyle() {
      if (this.isMinimalView) {
        return { position: 'relative', height: '100vh', width: '100%' };
      }
      if (this.isBrandedView) {
        return { position: 'relative', height: '100%', width: '100%' };
      }
      return { position: 'relative', height: 'auto', width: '100%' };
    },
    iframeStyle() {
      if (this.isMinimalView) {
        return { height: '100vh', width: '100%', border: 'none' };
      }
      if (this.isBrandedView) {
        return { height: '100%', width: '100%', border: 'none' };
      }
      return { height: '400px', width: '100%' };
    },
    videoPlyrOptions() {
      return {
        ratio: '16:9',
        fullscreen: {
          enabled: true,
          fallback: true,
          iosNative: true,
          container: '.video-player-container',
        },
      };
    },
    videoPoster() {
      return (
        this.data.video?.thumbnail ||
        this.data.video?.poster ||
        this.data.thumbnail ||
        this.data.poster ||
        undefined
      );
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initPlyrProgressBar();
    });
  },
  beforeDestroy() {
    clearTimeout(this.progressInitTimer);
    this.removeProgressListeners();
  },
  watch: {
    'data.file_upload': {
      immediate: true,
      handler() {
        this.resolveDocumentFileSize();
        this.$nextTick(() => {
          this.initPlyrProgressBar();
        });
      },
    },
    'data.file_type'() {
      this.resolveDocumentFileSize();
      this.$nextTick(() => {
        this.initPlyrProgressBar();
      });
    },
  },
  methods: {
    removeProgressListeners() {
      if (this.progressPlayer && this.progressHandlers) {
        Object.keys(this.progressHandlers).forEach(event => {
          this.progressPlayer.off(event, this.progressHandlers[event]);
        });
      }
      this.progressPlayer = null;
      this.progressHandlers = null;
      this.targetProgressBar = null;
    },
    initPlyrProgressBar() {
      clearTimeout(this.progressInitTimer);
      if (this._isDestroyed || this._isBeingDestroyed) return;
      if (this.data.file_type !== 'video') {
        this.removeProgressListeners();
        return;
      }
      const playerComponent = this.$refs.videoPlyr;
      if (!playerComponent) {
        this.progressInitTimer = setTimeout(() => this.initPlyrProgressBar(), 300);
        return;
      }

      const player = playerComponent.player;
      if (!player) {
        this.progressInitTimer = setTimeout(() => this.initPlyrProgressBar(), 300);
        return;
      }

      if (this.progressPlayer === player) return;
      this.removeProgressListeners();
      this.progressPlayer = player;
      this.videoDuration = 0;

      // Captura la duración tan pronto como esté disponible
      const tryCaptureDuration = () => {
        this.targetProgressBar = player.elements.controls
          ? player.elements.controls.querySelector('.plyr__progress')
          : null;
        const d = player.duration || (player.media && player.media.duration) || 0;
        if (Number.isFinite(d) && d > 0) {
          this.videoDuration = d;
        }
      };

      tryCaptureDuration();

      const updateTime = () => {
        this.currentVideoTime = player.currentTime || 0;
        // Respaldo: si todavía no tenemos duración, intentamos otra vez
        if (!this.videoDuration || this.videoDuration <= 0) {
          tryCaptureDuration();
        }
      };
      this.progressHandlers = {
        ready: tryCaptureDuration,
        loadedmetadata: tryCaptureDuration,
        durationchange: tryCaptureDuration,
        timeupdate: updateTime,
      };
      Object.keys(this.progressHandlers).forEach(event => {
        player.on(event, this.progressHandlers[event]);
      });
    },
    handleMarkerClick(marker) {
      if (marker && marker.time != null) {
        this.seekToTime(marker.time);
      }
      if (marker.type === 'note') {
        this.$emit('open-notes');
      } else if (marker.type === 'survey') {
        this.$emit('open-polls');
      }
    },
    handleMarkerHover({ marker, event }) {
      this.tooltip.marker = marker;
      this.tooltip.position = { x: event.clientX, y: event.clientY - 40 };
      this.tooltip.visible = true;
    },
    handleMarkerLeave() {
      this.tooltip.visible = false;
    },
    embedFillClass(defaultClass) {
      if (this.isMinimalView || this.isBrandedView) {
        return 'minimal-embed-fill branded-embed-fill';
      }
      return defaultClass;
    },
    async resolveDocumentFileSize() {
      if (this.data.file_type !== 'documento' || this.isPdfDocument) {
        this.resolvedFileSize = null;
        return;
      }

      if (this.data.file_size != null && this.data.file_size !== '') {
        this.resolvedFileSize = this.data.file_size;
        return;
      }

      this.fetchingFileSize = true;
      this.resolvedFileSize = await fetchRemoteFileSize(this.data.file_upload);
      this.fetchingFileSize = false;
    },
    downloadDocument() {
      if (!this.data.file_upload) {
        return;
      }

      const link = document.createElement('a');
      link.href = this.data.file_upload;
      link.download = this.documentFileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    toggleFullscreen() {
      if (this.$refs.fullscreen) {
        this.$refs.fullscreen.toggle();
      }
    },
    getCurrentTime() {
      const player = this.$refs.videoPlyr && this.$refs.videoPlyr.player;
      if (!player || !Number.isFinite(player.currentTime)) return 0;

      return Math.floor(player.currentTime);
    },
    seekToTime(seconds) {
      const player = this.$refs.videoPlyr && this.$refs.videoPlyr.player;
      if (!player) return;

      player.currentTime = Math.max(0, Math.floor(Number(seconds) || 0));
      if (typeof player.play === 'function') {
        player.play().catch(() => {});
      }
    },
    pauseVideo() {
      const player = this.$refs.videoPlyr && this.$refs.videoPlyr.player;
      if (player && typeof player.pause === 'function') {
        player.pause();
      }
    },
    playVideo() {
      const player = this.$refs.videoPlyr && this.$refs.videoPlyr.player;
      if (player && typeof player.play === 'function') {
        player.play().catch(() => {});
      }
    },
    emitVideoTimeUpdate(event) {
      const video = event?.target;
      const currentTime = Math.floor(Number(video?.currentTime || 0));

      // Actualizar currentVideoTime desde el evento nativo también
      this.currentVideoTime = video?.currentTime || 0;

      // Capturar duración desde el elemento de video nativo si aún no la tenemos
      if ((!this.videoDuration || this.videoDuration <= 0) && video?.duration && Number.isFinite(video.duration) && video.duration > 0) {
        this.videoDuration = video.duration;
      }

      if (currentTime === this.lastEmittedVideoSecond) return;

      this.lastEmittedVideoSecond = currentTime;
      this.$emit('time-update', currentTime);
    },
    emitVideoEnded() {
      this.$emit('video-ended');
    },
    onFullscreenChange(isFullscreen) {
      this.$emit('fullscreen-change', isFullscreen);
    },
    enterMinimalFullscreen() {
      if (this.data.file_type !== 'video') return;
      this.$nextTick(() => {
        const target =
          this.$el.querySelector('.minimal-video') ||
          this.$el.querySelector('.video-player-container') ||
          this.$el;
        if (this.$fullscreen && this.$fullscreen.support) {
          this.$fullscreen.request(target).catch(() => {});
        } else if (this.$refs.fullscreen) {
          this.$refs.fullscreen.toggle();
        }
      });
    },
  },
};
</script>

<style scoped>
.minimal-media-root {
  width: 100vw;
  height: 100vh;
  background: #000;
}

.branded-media-root {
  width: 100%;
  height: 100%;
  background: #000;
}

.branded-media-row,
.branded-media-col {
  height: 100%;
}

.branded-embed-fill {
  position: relative;
  width: 100%;
  height: 100%;
}

.minimal-embed-fill {
  position: relative;
  width: 100%;
  height: 100vh;
}

.minimal-embed-fill.branded-embed-fill {
  height: 100%;
}

.minimal-iframe-fill {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.minimal-video {
  width: 100%;
  height: 100%;
  max-height: 100vh;
  aspect-ratio: unset;
  min-height: 0;
}

.minimal-video >>> .vue-plyr,
.minimal-video >>> .plyr,
.minimal-video >>> .plyr__video-wrapper,
.minimal-video >>> video {
  height: 100% !important;
}

.branded-video {
  width: 100%;
  height: 100%;
  max-width: none;
  aspect-ratio: unset;
  min-height: 0;
}

.branded-video >>> .vue-plyr,
.branded-video >>> .plyr,
.branded-video >>> .plyr__video-wrapper,
.branded-video >>> video {
  height: 100% !important;
}

.video-player-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  min-height: 220px;
  overflow: visible;
}

.video-player-container >>> .vue-plyr,
.video-player-container >>> .plyr {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.video-player-container >>> .plyr__video-wrapper {
  height: 100% !important;
  background: #000;
}

.video-player-container >>> video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.video-player-actions {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 8;
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: auto;
}

@media (max-width: 640px) {
  .video-player-actions {
    top: 10px;
    right: 10px;
  }
}

.minimal-audio {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.canva-embed {
  position: relative;
  width: 100%;
  height: 0;
  padding-top: 83.8298%;
  padding-bottom: 48px;
  margin-top: 1.6em;
  margin-bottom: 0.9em;
  overflow: hidden;
  border-radius: 8px;
}

.canva-iframe {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border: none;
}

.genially-ratio {
  position: relative;
  padding-bottom: 56.25%;
  padding-top: 0;
  height: 0;
}

.genially-ratio iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.document-pdf-viewer,
.document-download-panel {
  width: 100%;
}

.document-pdf-viewer.branded-embed-fill {
  height: 100%;
}

.document-download-panel {
  display: flex;
  justify-content: center;
  padding: 24px 16px;
}

.document-download-panel--minimal {
  min-height: 100vh;
  align-items: center;
  background: #f5f5f5;
  padding: 32px 16px;
}

.document-download-card {
  width: 100%;
  max-width: 520px;
  border-radius: 16px !important;
}

.document-download-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #101820;
  word-break: break-word;
}

.document-download-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.document-download-hint {
  color: #666;
  font-size: 0.95rem;
  line-height: 1.5;
  max-width: 420px;
  margin: 0 auto;
}
</style>
